import {createContext, memo, use, useMemo} from 'react'

import type {PaginatedTotalCount} from '../../api/memex-items/paginated-views'
import {getGroupingMetadataFromServerGroupValue} from '../../features/grouping/get-grouping-metadata'
import type {GroupingMetadataWithSource} from '../../features/grouping/types'
import {useSliceBy} from '../../features/slicing/hooks/use-slice-by'
import {useSliceByDataQuery} from '../../state-providers/memex-items/queries/use-slice-by-data-query'

export type SlicerItemGroup = GroupingMetadataWithSource & {totalCount: PaginatedTotalCount}

export type SlicerItemsContextValue = {
  slicerItems: Array<SlicerItemGroup> | null
  /** Total number of slicer items before search filtering is applied. */
  totalSlicerItemCount: number
}

const SlicerItemsContext = createContext<SlicerItemsContextValue>({slicerItems: null, totalSlicerItemCount: 0})

export const useSlicerItems = () => {
  const contextValue = use(SlicerItemsContext)

  return contextValue
}

export const SlicerItemsProvider = memo<{children: React.ReactNode}>(function SlicerItemsProviderComponent({children}) {
  const sliceBy = useSliceBy()
  const {data: sliceByQueryData} = useSliceByDataQuery()
  const slicerItems: Array<SlicerItemGroup> = useMemo(() => {
    if (!sliceBy.sliceField || !sliceByQueryData) return []

    // Assuming we have a slice by field and data in the sliceByQuery,
    // we want to iterate over the slices and return the SlicerItemGroup
    // expected by the slice by panel
    const columnModel = sliceBy.sliceField
    return (
      sliceByQueryData.slices
        .map(sliceData => {
          const {sliceValue, sliceMetadata, totalCount} = sliceData
          const groupMetadataFromServerValue = getGroupingMetadataFromServerGroupValue(
            columnModel,
            sliceValue,
            sliceMetadata,
          )

          if (!groupMetadataFromServerValue) {
            return undefined
          }

          return {
            sourceObject: groupMetadataFromServerValue,
            value: sliceValue,
            totalCount,
          }
        })
        // if we were unable to find grouping metadata for the sliceValue, the map call above will
        // return undefined, so we want to filter those values out.
        .filter((g): g is SlicerItemGroup => !!g)
    )
  }, [sliceBy.sliceField, sliceByQueryData])

  const totalSlicerItemCount = useMemo(() => {
    if (!sliceBy.sliceField || !sliceByQueryData) return 0
    return sliceByQueryData.slices.length
  }, [sliceBy.sliceField, sliceByQueryData])

  const ctx = useMemo(
    () => ({
      slicerItems,
      totalSlicerItemCount,
    }),

    [slicerItems, totalSlicerItemCount],
  )

  return <SlicerItemsContext value={ctx}>{children}</SlicerItemsContext>
})
